import React, { forwardRef } from 'react'

import { twMerge } from 'tailwind-merge'

import Avatar from '@/components/common/Avatar'

import { useMentionList } from '@/extensions/Mention/hooks'
import type {
  MentionItem,
  MentionListProps,
  MentionListRef,
} from '@/extensions/Mention/types'

import type { User } from '@/types/User'

export type UserWithType = User &
  MentionItem & {
    mentionType: 'user'
  }

export const MentionList = forwardRef<
  MentionListRef,
  MentionListProps<UserWithType>
>((props, ref) => {
  const { selectedIndex, selectItem } = useMentionList(props, ref)

  return (
    <div
      className={twMerge(
        'flex flex-col gap-1 p-1 min-w-[212px] max-h-60 rounded-md border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900 overflow-y-auto',
      )}
    >
      {props.items.length ? (
        props.items.map((item, index) => (
          <button
            type="button"
            className={twMerge(
              'relative flex items-center gap-x-2 min-w-32 w-full rounded-[4px] cursor-pointer select-none py-1 px-2 outline-none text-[13px] text-primary-700 dark:text-primary-300',
              index === selectedIndex
                ? 'bg-primary-200 dark:bg-primary-800'
                : 'hover:bg-primary-100 dark:hover:bg-primary-800',
            )}
            key={index}
            // keep focus (and the caret) in the editor while clicking
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => selectItem(index)}
          >
            <div className="rounded-full">
              <Avatar
                src={item.picture_url}
                width={22}
                height={22}
                name={`${item.first_name} ${item.last_name}`}
                noTooltip={true}
                labelClassName="text-[9px]"
              />
            </div>
            {`${item.first_name} ${item.last_name}`}
          </button>
        ))
      ) : (
        <div
          className={twMerge(
            'relative flex items-center justify-between gap-x-2 min-w-32 w-full text-primary-900 dark:text-primary-100 rounded-[4px] cursor-default select-none px-2 py-1 text-sm outline-none',
          )}
        >
          <div className="text-primary-500 dark:text-primary-400">
            No members found.
          </div>
        </div>
      )}
    </div>
  )
})

MentionList.displayName = 'Mention.User.MentionList'
